import { Link } from "react-router-dom";
import {
  Truck,
  ShieldCheck,
  Package,
  CircleCheck,
  Sparkles,
  Calendar,
  Tag,
  Boxes,
  Recycle,
  BookOpen,
  GlassWater,
  Trash2,
  Briefcase,
  Moon,
  ArrowRight,
} from "lucide-react";
import BigDemenageurCTA from "@/components/BigDemenageurCTA";
import { getYvelinesCityPageBySlug } from "@/data/yvelinesCitiesContent";

const cityNameToSlug = (name) =>
  name
    .toLowerCase()
    .normalize("NFD")
    .replace(/[\u0300-\u036f]/g, "")
    .replace(/'/g, "")
    .replace(/\s+/g, "-");

const Sections = ({ city }) => (
  <>
    {/* ======= SECTION 2 — Engagements de livraison Plaisir ======= */}
    <section className="py-14 md:py-20 bg-[#EEF3F7] border-y border-[#cfdbe6]">
      <div className="container-cdm">
        <div className="max-w-3xl">
          <div className="overline" style={{ color: "#237cb5" }}>
            Nos engagements
          </div>
          <h2 className="mt-3 font-display font-extrabold text-[26px] sm:text-[30px] md:text-[36px] text-[#0f1d2b]">
            Nos engagements de livraison de vos cartons de déménagement
            {" "}{city.aCity}
          </h2>
          <p className="mt-5 text-[15px] md:text-base text-[#3a4856] leading-relaxed">
            Parce que votre temps est compté et que charger des volumes
            de cartons plats dans votre véhicule au milieu des grands
            axes de la ville est une contrainte évitable, notre service
            de livraison s&apos;engage pour vous.
          </p>
        </div>

        <div className="mt-8 grid grid-cols-1 md:grid-cols-3 gap-4 md:gap-5">
          {[
            {
              title: "Proximité et réactivité",
              body: "Nous desservons l'intégralité des quartiers de Plaisir (Les Gâtines, La Mare aux Saules, Le Buisson…) ainsi que les villes limitrophes comme Les Clayes-sous-Bois, Élancourt, Trappes, Jouars-Pontchartrain et Neauphle-le-Château.",
              Icon: Truck,
            },
            {
              title: "Ponctualité absolue",
              body: "Pas de créneaux flous qui bloquent votre journée. Nous définissons ensemble un rendez-vous précis et rigoureusement respecté pour vous permettre de planifier vos cartons à la minute près.",
              Icon: Calendar,
            },
            {
              title: "Hygiène et intégrité",
              body: "Toutes nos fournitures sont acheminées à plat, sous film de protection, parfaitement sèches et propres, prêtes pour un montage immédiat.",
              Icon: ShieldCheck,
            },
          ].map(({ title, body, Icon }) => (
            <div
              key={title}
              className="bg-white rounded-2xl border border-[#cfdbe6] p-5 md:p-6"
            >
              <div className="flex items-center gap-2.5">
                <span className="inline-flex items-center justify-center h-10 w-10 rounded-full bg-[#237cb5] text-white">
                  <Icon className="h-5 w-5" strokeWidth={2.4} />
                </span>
                <h3 className="font-display font-extrabold text-[15px] text-[#0f1d2b] leading-tight">
                  {title}
                </h3>
              </div>
              <p className="mt-3 text-[14px] text-[#3a4856] leading-relaxed">
                {body}
              </p>
            </div>
          ))}
        </div>
      </div>
    </section>

    {/* ======= SECTION 3 — Cartons reconditionnés à -40% ======= */}
    <section className="py-14 md:py-20 bg-white">
      <div className="container-cdm">
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-8 items-start">
          <div className="lg:col-span-7">
            <div className="overline" style={{ color: "#237cb5" }}>
              Offre éco-malin
            </div>
            <h2 className="mt-3 font-display font-extrabold text-[26px] sm:text-[30px] md:text-[36px] text-[#0f1d2b]">
              L&apos;opportunité éco-malin : nos cartons reconditionnés
              à&nbsp;-40%
            </h2>
            <p className="mt-5 text-[15px] md:text-base text-[#3a4856] leading-relaxed">
              Faire un geste pour la planète tout en réalisant de sacrées
              économies, c&apos;est désormais possible {city.aCity} grâce
              à notre filière exclusive de reconditionnement.
            </p>

            <div className="mt-6 space-y-4">
              {[
                {
                  title: "Une seule utilisation au compteur",
                  body: "Ces cartons professionnels n'ont servi qu'une seule et unique fois lors de transferts d'entreprises ou de déménagements de standing. Ils sont quasi-neufs.",
                  Icon: Recycle,
                },
                {
                  title: "Triés et validés par nos experts",
                  body: "Chaque boîte est inspectée manuellement. Nous écartons impitoyablement tout carton présentant la moindre faiblesse, pliure ou trace d'humidité pour ne garder que le top du top.",
                  Icon: ShieldCheck,
                },
                {
                  title: "Une remise immédiate de -40%",
                  body: "Vous profitez de la robustesse d'un carton de qualité industrielle (simple ou double cannelure) au prix de l'occasion. C'est le choix intelligent et éco-responsable le plus prisé par nos clients !",
                  Icon: Tag,
                },
              ].map(({ title, body, Icon }) => (
                <div
                  key={title}
                  className="flex items-start gap-4 bg-[#EEF3F7] border border-[#cfdbe6] rounded-2xl p-5 md:p-6"
                >
                  <span className="flex items-center justify-center shrink-0 h-11 w-11 rounded-full bg-[#237cb5] text-white">
                    <Icon className="h-5 w-5" strokeWidth={2.4} />
                  </span>
                  <div>
                    <h3 className="font-display font-extrabold text-[15.5px] text-[#0f1d2b] leading-tight">
                      {title}
                    </h3>
                    <p className="mt-2 text-[14px] text-[#3a4856] leading-relaxed">
                      {body}
                    </p>
                  </div>
                </div>
              ))}
            </div>

            <Link
              to="/cartons-reconditionnes"
              className="mt-7 inline-flex items-center gap-2 h-11 px-5 rounded-xl bg-[#237cb5] hover:bg-[#1f6da0] text-white font-bold text-[14px] transition-colors"
              data-testid="city-plaisir-cta-reconditionnes"
            >
              <Recycle className="h-4 w-4" strokeWidth={2.6} />
              Voir les cartons reconditionnés
              <ArrowRight className="h-4 w-4" strokeWidth={2.6} />
            </Link>
          </div>

          <div className="lg:col-span-5 bg-[#EEF3F7] border border-[#cfdbe6] rounded-3xl p-6">
            <div className="flex items-center gap-3">
              <span className="flex items-center justify-center h-12 w-12 rounded-full bg-[#237cb5] text-white">
                <Recycle className="h-6 w-6" strokeWidth={2.4} />
              </span>
              <div className="font-display font-extrabold text-[20px] text-[#0f1d2b] leading-tight">
                Économisez jusqu&apos;à&nbsp;40%
              </div>
            </div>
            <p className="mt-4 text-[14px] text-[#3a4856] leading-relaxed">
              La robustesse d&apos;un carton pro neuf, au prix de
              l&apos;occasion. Triés à la main, quasi-neufs, prêts à
              l&apos;emploi.
            </p>
            <div className="mt-5 space-y-2.5 text-[13px] text-[#3a4856]">
              <div className="flex items-center gap-2">
                <CircleCheck className="h-4 w-4 text-[#237cb5]" strokeWidth={2.6} />
                Une seule utilisation au compteur
              </div>
              <div className="flex items-center gap-2">
                <CircleCheck className="h-4 w-4 text-[#237cb5]" strokeWidth={2.6} />
                Sélection drastique à l&apos;unité
              </div>
              <div className="flex items-center gap-2">
                <CircleCheck className="h-4 w-4 text-[#237cb5]" strokeWidth={2.6} />
                Simple ou double cannelure
              </div>
              <div className="flex items-center gap-2">
                <Sparkles className="h-4 w-4 text-[#237cb5]" strokeWidth={2.6} />
                Geste éco-responsable
              </div>
            </div>
          </div>
        </div>
      </div>
    </section>

    {/* ======= CTA Big Déménagement (au milieu) ======= */}
    <section className="py-12 bg-[#EEF3F7] border-y border-[#cfdbe6]">
      <div className="container-cdm">
        <BigDemenageurCTA testId={`city-${city.slug}-bigdem-cta-middle`} />
      </div>
    </section>

    {/* ======= SECTION 4 — Cartons lourds ======= */}
    <section className="py-14 md:py-20 bg-white">
      <div className="container-cdm">
        <div className="max-w-3xl">
          <div className="overline" style={{ color: "#237cb5" }}>
            Charges lourdes
          </div>
          <h2 className="mt-3 font-display font-extrabold text-[26px] sm:text-[30px] md:text-[36px] text-[#0f1d2b]">
            Les cartons lourds : l&apos;armure indispensable pour vos
            objets denses
          </h2>
          <p className="mt-5 text-[15px] md:text-base text-[#3a4856] leading-relaxed">
            On fait souvent l&apos;erreur de remplir de grands cartons
            avec des objets lourds, ce qui mène droit à la rupture du
            fond. Pour vos éléments les plus denses, nos cartons
            spécifiques prennent le relais.
          </p>
        </div>

        <div className="mt-8 grid grid-cols-1 md:grid-cols-2 gap-4 md:gap-5">
          {[
            {
              title: "Le carton livres et archives",
              body: "De format plus compact, il est conçu avec une double cannelure renforcée. Il est idéal pour regrouper vos romans, encyclopédies, dossiers administratifs ou collections de disques sans que le carton ne devienne intransportable ou ne se déforme.",
              Icon: BookOpen,
            },
            {
              title: "Le carton vaisselle et objets denses",
              body: "Épaissis pour résister à la pression et à l'écrasement vertical, ces cartons permettent d'empiler vos appareils électroménagers, votre outillage ou vos bibelots en plomb en toute sécurité au fond du camion, servant de base solide pour le reste du chargement.",
              Icon: GlassWater,
            },
          ].map(({ title, body, Icon }) => (
            <div
              key={title}
              className="flex items-start gap-4 bg-[#EEF3F7] border border-[#cfdbe6] rounded-2xl p-5 md:p-6"
            >
              <span className="flex items-center justify-center shrink-0 h-11 w-11 rounded-full bg-[#237cb5] text-white">
                <Icon className="h-5 w-5" strokeWidth={2.4} />
              </span>
              <div>
                <h3 className="font-display font-extrabold text-[15.5px] text-[#0f1d2b] leading-tight">
                  {title}
                </h3>
                <p className="mt-2 text-[14px] text-[#3a4856] leading-relaxed">
                  {body}
                </p>
              </div>
            </div>
          ))}
        </div>

        <div className="mt-8">
          <Link
            to="/cartons-demenagement"
            className="inline-flex items-center gap-2 h-11 px-5 rounded-xl bg-[#237cb5] hover:bg-[#1f6da0] text-white font-bold text-[14px] transition-colors"
            data-testid="city-plaisir-cta-lourds"
          >
            <Boxes className="h-4 w-4" strokeWidth={2.6} />
            Voir tous les cartons renforcés
            <ArrowRight className="h-4 w-4" strokeWidth={2.6} />
          </Link>
        </div>
      </div>
    </section>

    {/* ======= SECTION 5 — Rétroplanning J-30 / J-15 / J-2 ======= */}
    <section className="py-14 md:py-20 bg-[#EEF3F7] border-y border-[#cfdbe6]">
      <div className="container-cdm">
        <div className="max-w-3xl">
          <div className="overline" style={{ color: "#237cb5" }}>
            Rétroplanning
          </div>
          <h2 className="mt-3 font-display font-extrabold text-[26px] sm:text-[30px] md:text-[36px] text-[#0f1d2b]">
            L&apos;organisation du déménagement : le rétroplanning pour
            rester zen
          </h2>
          <p className="mt-5 text-[15px] md:text-base text-[#3a4856] leading-relaxed">
            Pour qu&apos;un grand déménagement {city.aCity} se déroule
            sans accroc, une bonne méthode de travail est essentielle les
            semaines précédant le jour J.
          </p>
        </div>

        <div className="mt-8 grid grid-cols-1 md:grid-cols-3 gap-4 md:gap-5">
          {[
            {
              n: "J-30",
              title: "Le grand tri sélectif",
              body: "Ne déménagez pas ce que vous n'utilisez plus. Vendez, donnez ou recyclez. Moins vous aurez de volume, moins vous achèterez de cartons et plus vite le camion sera chargé.",
              Icon: Trash2,
            },
            {
              n: "J-15",
              title: "L'emballage des pièces non essentielles",
              body: "Commencez à mettre en boîte tout ce qui ne sert pas au quotidien : livres, bibelots, vêtements hors saison, vaisselle de réception.",
              Icon: Briefcase,
            },
            {
              n: "J-2",
              title: "Le kit de survie pour la première nuit",
              body: "Préparez un carton spécifique (et gardez-le avec vous) contenant le nécessaire pour vos premières 24h dans votre nouveau logement : chargeurs, trousse de secours, produits de toilette, quelques vêtements de rechange, et de quoi préparer un café ou un repas rapide.",
              Icon: Moon,
            },
          ].map(({ n, title, body, Icon }) => (
            <div
              key={n}
              className="flex items-start gap-4 bg-white border border-[#cfdbe6] rounded-2xl p-5 md:p-6"
            >
              <span className="flex items-center justify-center shrink-0 h-11 w-11 rounded-full bg-[#237cb5] text-white">
                <Icon className="h-5 w-5" strokeWidth={2.4} />
              </span>
              <div>
                <div className="text-[11px] uppercase tracking-[0.18em] font-bold text-[#237cb5]">
                  {n}
                </div>
                <h3 className="mt-1 font-display font-extrabold text-[16px] text-[#0f1d2b] leading-tight">
                  {title}
                </h3>
                <p className="mt-2 text-[14px] text-[#3a4856] leading-relaxed">
                  {body}
                </p>
              </div>
            </div>
          ))}
        </div>
      </div>
    </section>

    {/* ======= SECTION 6 — Couverture département / Satellites ======= */}
    <section className="py-14 md:py-20 bg-white">
      <div className="container-cdm">
        <div className="max-w-3xl">
          <div className="overline" style={{ color: "#237cb5" }}>
            Couverture {city.deptCode}
          </div>
          <h2 className="mt-3 font-display font-extrabold text-[26px] sm:text-[30px] md:text-[36px] text-[#0f1d2b]">
            Nos zones de livraison de cartons dans les Yvelines (78)
          </h2>
          <p className="mt-5 text-[15px] md:text-base text-[#3a4856] leading-relaxed">
            Nous proposons la livraison de cartons de déménagement et de
            matériel d&apos;emballage dans de nombreuses communes des
            Yvelines. Grâce à notre service de proximité, vous recevez
            rapidement les fournitures nécessaires pour préparer votre
            déménagement en toute simplicité.
          </p>
          <p className="mt-4 text-[15px] md:text-base text-[#3a4856] leading-relaxed">
            Retrouvez ci-dessous les principales villes du département où
            nous assurons régulièrement la livraison de nos cartons et
            accessoires de déménagement à proximité de {city.name}&nbsp;:
          </p>
        </div>

        <ul className="mt-6 flex flex-wrap gap-x-5 gap-y-2.5 text-[15px] md:text-base">
          {city.nearbyCities.map((name) => {
            const idSlug = cityNameToSlug(name);
            const target = getYvelinesCityPageBySlug(idSlug);
            return (
              <li key={name}>
                {target ? (
                  <Link
                    to={`/carton-demenagement-${target.slug}`}
                    className="inline-flex items-center font-semibold text-[#237cb5] underline underline-offset-4 decoration-[#237cb5]/40 hover:decoration-[#237cb5] hover:text-[#0f1d2b] transition-colors"
                    data-testid={`city-plaisir-nearby-${idSlug}`}
                  >
                    Carton déménagement {name}
                  </Link>
                ) : (
                  <span
                    className="inline-flex items-center font-semibold text-[#5b6b7a]"
                    data-testid={`city-plaisir-nearby-${idSlug}`}
                  >
                    Carton déménagement {name}
                  </span>
                )}
              </li>
            );
          })}
        </ul>

        <div className="mt-8">
          <Link
            to={`/cartons-demenagement/${city.deptSlug}`}
            className="inline-flex items-center gap-2 h-11 px-5 rounded-xl bg-[#EEF3F7] border border-[#cfdbe6] hover:border-[#237cb5] hover:text-[#237cb5] text-[#0f1d2b] font-bold text-[14px] transition-colors"
            data-testid="city-plaisir-cta-back-dept"
          >
            <Package className="h-4 w-4" strokeWidth={2.6} />
            Voir la page {city.deptName} ({city.deptCode})
          </Link>
        </div>
      </div>
    </section>
  </>
);

export default { Sections };
